import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

interface RouteErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
  onGoHome: () => void;
  retriesRemaining: number;
}

function RouteErrorFallback({ 
  error, 
  onReset, 
  onGoHome,
  retriesRemaining 
}: RouteErrorFallbackProps) {
  const canRetry = retriesRemaining > 0;

  return (
    <div className="flex-1 flex items-center justify-center p-4 min-h-[60vh]">
      <Card className="max-w-md w-full text-center">
        <CardHeader className="pb-4">
          <div className="mx-auto w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
            <AlertTriangle className="w-5 h-5 text-destructive" />
          </div>
          <CardTitle className="text-lg">Page failed to load</CardTitle>
          <CardDescription className="mt-1">
            Something went wrong loading this page. You can try again or go back home.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={onGoHome} 
              className="flex-1 gap-2"
            >
              <Home className="w-4 h-4" />
              Go Home
            </Button>
            {canRetry ? (
              <Button 
                onClick={onReset} 
                className="flex-1 gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </Button>
            ) : (
              <Button 
                disabled 
                className="flex-1 gap-2"
              >
                No retries left
              </Button>
            )}
          </div>
          
          {error && process.env.NODE_ENV === 'development' && (
            <div className="mt-3 p-2 rounded-md bg-muted text-left">
              <p className="text-xs font-mono text-muted-foreground break-all">
                {error.message}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  fallbackRoute?: string;
  maxRetries?: number;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

/**
 * Route-level error boundary that catches errors within a route while
 * preserving the app shell (navigation, bottom nav, etc.).
 * 
 * Provides "Go Home" and "Try Again" recovery options.
 * Limits retries to prevent infinite loops on persistent errors.
 */
class RouteErrorBoundaryInner extends React.Component<
  RouteErrorBoundaryProps & { navigate: (path: string) => void },
  RouteErrorBoundaryState
> {
  static defaultProps = {
    fallbackRoute: '/',
    maxRetries: 3,
  };

  constructor(props: RouteErrorBoundaryProps & { navigate: (path: string) => void }) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[RouteErrorBoundary] Caught error:', error);
    console.error('[RouteErrorBoundary] Component stack:', errorInfo.componentStack);
    
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    const maxRetries = this.props.maxRetries ?? 3;
    
    if (this.state.retryCount < maxRetries) {
      this.setState(prev => ({ 
        hasError: false, 
        error: null, 
        retryCount: prev.retryCount + 1 
      }));
    }
  };

  handleGoHome = (): void => {
    const fallbackRoute = this.props.fallbackRoute ?? '/';
    this.setState({ hasError: false, error: null, retryCount: 0 });
    this.props.navigate(fallbackRoute);
  };

  render(): React.ReactNode {
    const maxRetries = this.props.maxRetries ?? 3;
    const retriesRemaining = maxRetries - this.state.retryCount;

    if (this.state.hasError) {
      return (
        <RouteErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
          retriesRemaining={retriesRemaining}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper component that provides navigation context to the class component
 */
export function RouteErrorBoundary(props: RouteErrorBoundaryProps) {
  const navigate = useNavigate();
  return <RouteErrorBoundaryInner {...props} navigate={navigate} />;
}
